import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const CATEGORY_TABS = ['All', 'Necklaces', 'Earrings', 'Bridal Collection'];

const Products = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const selectedCategory = searchParams.get('category') || 'All';

  const fetchProducts = useCallback(async () => {
    try {
      const query = selectedCategory !== 'All'
        ? { params: { category: selectedCategory } }
        : undefined;
      const { data } = await axios.get(`${BACKEND_URL}/api/products`, query);
      const productsWithRatings = await Promise.all(
        data.map(async (product) => {
          try {
            const statsRes = await axios.get(`${BACKEND_URL}/api/reviews/${product.id}/stats`);
            return { ...product, reviewStats: statsRes.data };
          } catch (error) {
            return { ...product, reviewStats: { average_rating: 0, total_reviews: 0 } };
          }
        })
      );
      setProducts(productsWithRatings);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  }, [selectedCategory]);

  const fetchWishlist = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/wishlist`, { withCredentials: true });
      setWishlistItems(data.items || []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    }
  }, [user]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchProducts();
      await fetchWishlist();
      setLoading(false);
    };
    loadData();
  }, [fetchProducts, fetchWishlist]);

  const handleCategoryChange = (category) => {
    if (category === 'All') {
      setSearchParams({});
      return;
    }
    setSearchParams({ category });
  };

  if (loading) {
    return (
      <div className="heritage-page min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c79a35]"></div>
      </div>
    );
  }

  return (
    <div className="heritage-page min-h-screen flex flex-col">
      <Header />
      
      <main className="heritage-shell flex-1">
        <div className="text-center mb-12">
          <p className="heritage-kicker mb-2">Our Collection</p>
          <h1 className="heritage-title text-4xl sm:text-5xl lg:text-6xl" data-testid="products-title">
            South Indian Jewelry
          </h1>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-10" data-testid="products-category-tabs">
          {CATEGORY_TABS.map((category) => {
            const isActive = selectedCategory === category;
            return (
              <button
                key={category}
                type="button"
                onClick={() => handleCategoryChange(category)}
                className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-[#c9a45b] bg-[#2b1c17] text-[#f3e7d1]'
                    : 'border-[#dcc9ad] bg-[rgba(255,250,242,0.88)] text-[#6a5448] hover:border-[#c79a35] hover:text-[#4b2e24]'
                }`}
                data-testid={`products-category-tab-${category.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {category}
              </button>
            );
          })}
        </div>

        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#6a5448] text-lg">No products available in this category right now.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-testid="products-grid">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                inWishlist={wishlistItems.includes(product.id)}
                onWishlistToggle={fetchWishlist}
                averageRating={product.reviewStats?.average_rating || 0}
                totalReviews={product.reviewStats?.total_reviews || 0}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Products;
